import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Spin, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import get from "lodash/get";
import { deleteApi, postApi } from "../../redux/rest/restSlice";
import { DELETE_PRICING, LIST_PRICING } from "../../constants/actionTypes";
import { pricingTable } from "../../constants/pricingTables";
import { PERMISSIONS } from "../../constants/permissions";
import { TABLE_PAGINATION } from "../../utilities/helper";
import Can from "../Can";
import DataTable from "../DataTable";
import AddEditPricing from "./addEditPricing";

const Pricing = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [modalInfo, setModalInfo] = useState({
    visible: false,
    data: null,
  });

  const dispatch = useDispatch();

  const fetchPricingList = useCallback(() => {
    setLoading(true);
    dispatch(postApi({ endpoint: LIST_PRICING, payload: {} }))
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        setData(get(result, "data.data", []));
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchPricingList();
  }, [fetchPricingList]);

  // The list endpoint returns every pricing, so the search filters locally
  const onSearch = (value) => {
    setAppliedSearch(value);
  };

  const clearSearch = () => {
    setSearchValue("");
    onSearch("");
  };

  const openModal = (record = null) => {
    setModalInfo({ visible: true, data: record });
  };

  const closeModal = () => {
    setModalInfo({ visible: false, data: null });
  };

  const onSuccess = () => {
    closeModal();
    fetchPricingList();
  };

  const onDelete = (record) => {
    setLoading(true);
    dispatch(deleteApi({ endpoint: `${DELETE_PRICING}/${record.id}` }))
      .then(unwrapResult)
      .then(() => {
        setLoading(false);
        message.success("Pricing Deleted Successfully !!");
        fetchPricingList();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const term = appliedSearch.trim().toLowerCase();
  const rows = term
    ? (data || []).filter((item) =>
        get(item, "pricing_name", "").toLowerCase().includes(term)
      )
    : data || [];

  const firstLoad = loading && !get(data, "length");

  return (
    <Spin spinning={loading && !firstLoad}>
      <div className="list-toolbar">
        <div className="list-toolbar__main">
          <Input.Search
            className="list-toolbar__search"
            placeholder="Search by pricing name"
            allowClear
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={onSearch}
          />
        </div>

        <Can permissions={[PERMISSIONS.CREATE_PRICINGS]}>
          <div className="list-toolbar__actions">
            <Button
              type="primary"
              className="default-primary-btn"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              Create Pricing
            </Button>
          </div>
        </Can>
      </div>

      <DataTable
        skeleton={firstLoad}
        scrollX={1100}
        rowKey="id"
        className="mt-20"
        dataSource={rows}
        columns={pricingTable(openModal, onDelete)}
        pagination={TABLE_PAGINATION}
        emptyProps={{
          noun: "pricings",
          search: appliedSearch,
          onClearSearch: clearSearch,
        }}
      />

      <Modal
        width={640}
        title={get(modalInfo, "data") ? "Update Pricing" : "Create Pricing"}
        destroyOnClose
        open={get(modalInfo, "visible")}
        onCancel={closeModal}
        footer={false}
      >
        <AddEditPricing
          modalInfo={modalInfo}
          setModalInfo={closeModal}
          onSuccess={onSuccess}
        />
      </Modal>
    </Spin>
  );
};

export default Pricing;
